import React, { useMemo, useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet";
import { routeList as defaultRouteList } from "../../config/routeConfig";
import LagunaCreekLogo from "../../assets/lagunacreek-logo.svg";
import ReactGA from "react-ga4";

const GA_MEASUREMENT_ID = "GTM-PG4P97B3";

export const Header = () => {
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [routeList, setRouteList] = useState(defaultRouteList);

  const [headerSettings, setHeaderSettings] = useState({
    favicon: "/favicon.png",
    footer_about_us_text:
      "Boosting the Indigenous economy by keeping more tourism, retail and hospitality dollars on Indigenous land.",
    footer_logo: "/logo.png",
    google_tag_manager_body: `
      <noscript>
        <iframe src="https://www.googletagmanager.com/ns.html?id=GTM-PG4P97B3"
          height="0" width="0" style="display:none;visibility:hidden"></iframe>
      </noscript>`,
    google_tag_manager_head: `
      <script>
        (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
        'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer','GTM-PG4P97B3');
      </script>`,
    header_button_link: "/contact-us",
    header_button_text: "Request A Demo",
    header_logo: LagunaCreekLogo,
  });

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  // Fetch Header Settings and Menu
  useEffect(() => {
    const fetchHeaderSettings = async () => {
      try {
        const res = await fetch(
          "https://www.lagunacreek.io/blog/wp-json/custom/v1/header-setting"
        );
        const data = await res.json();
        setHeaderSettings((prev) => ({ ...prev, ...data }));
        console.log(
          data?.google_tag_manager_head,
          "data?.google_tag_manager_head"
        );

        ReactGA.initialize(data?.google_tag_manager_head ?? GA_MEASUREMENT_ID, {
          gtagUrl: "https://www.googletagmanager.com/gtm.js",
        });
        ReactGA.send({
          hitType: "pageview",
          page: location.pathname + location.search,
        });
      } catch (err) {
        console.warn("Using default header settings.", err);
      }
    };

    const fetchMenu = async () => {
      try {
        const res = await fetch(
          "https://www.lagunacreek.io/blog/wp-json/custom/v1/menu/primary_menu"
        );
        const data = await res.json();

        if (Array.isArray(data)) {
          const apiRoutes = data.map((item) => {
            const matchedRoute = defaultRouteList.find(
              (r) => r.path === item.url
            );
            return {
              ...matchedRoute,
              path: item.url,
              menuLabel: item.title,
              menuTitle: item.acf?.page_title || matchedRoute?.menuTitle,
              meta_keyword:
                item.acf?.meta_keyword || matchedRoute?.meta_keyword,
              meta_description:
                item.acf?.meta_description || matchedRoute?.meta_description,
              showMenuBar: matchedRoute?.showMenuBar ?? true,
              Component: matchedRoute?.Component || null,
            };
          });
          setRouteList(apiRoutes);
        }
      } catch (err) {
        console.warn("Using default menu.", err);
      }
    };

    fetchHeaderSettings();
    fetchMenu();
  }, []);

  const navigationItems = routeList.filter((a) => a.showMenuBar);

  const pageTitle = useMemo(() => {
    const activeRoute = routeList.find((r) => r.path === location.pathname);
    return activeRoute?.menuTitle || "Laguna Creek";
  }, [location.pathname, routeList]);

  const metaDescription = useMemo(() => {
    const activeRoute = routeList.find((r) => r.path === location.pathname);
    return (
      activeRoute?.meta_description || "Boosting the Indigenous economy..."
    );
  }, [location.pathname, routeList]);

  const metaKeyword = useMemo(() => {
    const activeRoute = routeList.find((r) => r.path === location.pathname);
    return activeRoute?.meta_keyword || "PMS, POS, Tour Booking Platform";
  }, [location.pathname, routeList]);

  const handleClick = (e) => {
    if (location.pathname === "/contact-us") {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };
  return (
    <>
      {/* Google Tag Manager Head */}
      <Helmet>
        <meta charSet="utf-8" />
        <title>{pageTitle}</title>
        <meta name="keywords" content={metaKeyword} />
        <meta name="description" content={metaDescription} />
        <link rel="icon" href={headerSettings.favicon} />
        <meta
          name="google-site-verification"
          content="hiapfl5TqsfMfYAJztaptv_BzMWssGmnCS7zXWH3HdM"
        />
      </Helmet>

      {/* Google Tag Manager Body (noscript) */}
      {/* <div dangerouslySetInnerHTML={{ __html: headerSettings.google_tag_manager_body }} /> */}
      <div className="inline-flex items-center rounded-md bg-white px-2 py-1 text-xs font-medium text-[#FAA42A] ring-1 ring-[#FAA42A] ring-inset hidden"></div>
      <header className="sticky top-0 bg-white py-2 border-b border-[#E7E7E7] z-20">
        <div className="w-full max-w-[1570px] px-4 mx-auto">
          <div className="flex justify-between items-center -mx-4">
            <div className="px-4">
              <Link to="/">
                <img
                  src={headerSettings.header_logo}
                  alt="Laguna Creek Logo"
                  height="50"
                  className="w-auto"
                />
              </Link>
            </div>
            <div className="px-4 flex items-center">
              <ul className="hidden lg:flex items-center gap-5 xl:gap-10 mr-10">
                {navigationItems.map((item, i) => {
                  const active = location.pathname === item.path;
                  return (
                    <li
                      key={i}
                      className={`relative after:content-[''] after:absolute after:-bottom-[26px] after:right-0 after:left-0 after:mx-auto after:bg-[#faa42a] ${
                        active ? "after:w-full" : "after:w-0"
                      } after:h-[3px] after:transition-all after:ease-in-out after:duration-500 hover:after:w-full`}
                    >
                      <Link to={item.path}>
                        <span
                          className={`text-sm font-normal leading-5 transition-all ease-in-out duration-500 hover:text-[#5FA3B2] ${
                            active ? "text-[#5FA3B2]" : "text-[#333333]"
                          }`}
                          dangerouslySetInnerHTML={{ __html: item.menuLabel }}
                        ></span>
                      </Link>
                    </li>
                  );
                })}
                <li>
                  <a
                    href="https://lagunacreek.io/blog/"
                    className="text-sm font-normal leading-5 text-[#333333] transition-all hover:text-[#5FA3B2]"
                  >
                    Blog
                  </a>
                </li>
              </ul>

              <Link
                to={headerSettings.header_button_link}
                onClick={handleClick}
                className="hidden sm:inline-block text-sm font-bold leading-5 text-white bg-[#5FA3B2] px-8 py-3 rounded-xl transition-all hover:bg-[#FAA42A]"
              >
                {headerSettings.header_button_text}
              </Link>

              <button
                type="button"
                className={`lg:hidden ml-0 sm:ml-5 w-[26px] h-[26px] relative right-4 group ${
                  isMenuOpen ? "active" : ""
                }`}
                onClick={toggleMenu}
              >
                <span className="absolute h-[3px] w-full bg-[#333] top-[2px]"></span>
                <span className="absolute h-[3px] w-full bg-[#333] top-[10px]"></span>
                <span className="absolute h-[3px] w-full bg-[#333] top-[10px]"></span>
                <span className="absolute h-[3px] w-full bg-[#333] top-[18px]"></span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed top-0 left-0 right-0 bottom-0 bg-black opacity-60 z-20 ${
          isMenuOpen ? "" : "hidden"
        }`}
      ></div>

      {/* Mobile Menu Panel */}
      <div
        className={`fixed z-20 top-0 bottom-0 w-full max-w-[375px] bg-white transition-all duration-300 ${
          isMenuOpen ? "!right-0" : "-right-[375px]"
        }`}
      >
        <div className="py-8 px-4 overflow-auto h-screen">
          <div className="leading-[0] text-end mb-8">
            <button
              type="button"
              className={`lg:hidden ml-0 sm:ml-5 w-[26px] h-[26px] relative group ${
                isMenuOpen ? "active" : ""
              }`}
              onClick={toggleMenu}
            >
              <span className="absolute h-[3px] w-full bg-[#333333] left-0 top-[2px] transition duration-300 ease-in-out group-[.active]:w-0"></span>
              <span className="absolute h-[3px] w-full bg-[#333333] left-0 top-[10px] rotate-45 transition duration-300 ease-in-out"></span>
              <span className="absolute h-[3px] w-full bg-[#333333] left-0 top-[10px] -rotate-45 transition duration-300 ease-in-out"></span>
              <span className="absolute h-[3px] w-full bg-[#333333] left-0 top-[18px] transition duration-300 ease-in-out group-[.active]:w-0"></span>
            </button>
          </div>
          <ul className="flex flex-col gap-y-[10px]">
            {navigationItems.map((item, index) => {
              const activeRoute = location.pathname === item.path;
              return (
                <li key={index} className="bg-[#f5f5f5] py-3 px-4">
                  <Link to={item.path} onClick={toggleMenu}>
                    <span
                      className={`text-sm font-normal leading-5 transition-all ease-in-out duration-500 hover:text-[#5FA3B2] ${
                        activeRoute ? "text-[#5FA3B2]" : "text-[#333333]"
                      }`}
                      dangerouslySetInnerHTML={{ __html: item.menuLabel }}
                    ></span>
                  </Link>
                </li>
              );
            })}
            <li className="bg-[#f5f5f5] py-3 px-4">
              <a
                href="https://lagunacreek.io/blog/"
                className="text-sm font-bold leading-5 text-[#333333] uppercase transition-all ease-in-out duration-500 hover:text-[#faa42a]"
              >
                Blog
              </a>
            </li>
          </ul>
        </div>
      </div>
    </>
  );
};

export default Header;
